import { LCBatchesPresentationOrderEnum } from '@kaltura-ott/tvpil';
import { container } from 'tsyringe';

import type { ChannelsPersonalization } from '../../rhino/data/channel/personalization';
import type { ChannelsSettings } from '../../rhino/data/channel/settings';
import { getFavoriteChannelsQueryData } from '../../rhino/data/favorite/queries';
import type { PersonalLineupEntry } from '../../rhino/phoenix';
import { sortByOrderBatches } from '../../utils/sorting';
import { Linear } from '../entities';
import type { LinearRepository } from '../linearRepository/linear-repository';

export const composeLinears = (
  linears: Array<Linear>,
  personalization: ChannelsPersonalization,
  settings: ChannelsSettings,
  collection?: Array<Linear>,
) => {
  if ('favoriteChannels' in personalization && collection) {
    return composeFavoriteChannels(linears, settings.orderBatches, collection);
  }

  if ('favoriteChannels' in personalization && !collection) {
    return sortByOrderBatches(linears, settings.orderBatches);
  }

  if ('personalLineup' in personalization && personalization.personalLineup) {
    return composePersonalLineup(linears, personalization.personalLineup.entries);
  }

  return linears;
};

const composeFavoriteChannels = (
  channels: Array<Linear>,
  orderBatches: ChannelsSettings['orderBatches'] = [],
  collection: Array<Linear>,
) => {
  const collectionSet = new Map(channels.map((c) => [c.id, c]));

  const data = getFavoriteChannelsQueryData();

  if (!data) {
    return channels;
  }

  const linears: Array<Linear> = [];

  const { favoriteList, favoritePositions } = data;

  const sortByFavoriteEnabled = orderBatches.includes(LCBatchesPresentationOrderEnum.fav);

  if (sortByFavoriteEnabled) {
    for (let i = 0; i < favoriteList.length; i++) {
      if (collectionSet.has(favoriteList[i].assetId)) {
        linears.push(collectionSet.get(favoriteList[i].assetId)!);
      }
    }
  }

  for (let i = 0; i < collection.length; i++) {
    if (sortByFavoriteEnabled && favoritePositions.has(collection[i].id)) {
      continue;
    }

    linears.push(collection[i]);
  }

  return linears;
};

function composePersonalLineup(channels: Array<Linear>, personalLineup: Array<PersonalLineupEntry>) {
  const linearRepository = container.resolve<LinearRepository>('linearRepository');
  const pclChannels = new Set(personalLineup.map(({ channelId }) => channelId));
  const oclChannels = new Set(channels.map((i) => i.id));

  const newLinears: Array<Linear> = [];
  const linears: Array<Linear> = [];

  // operator can add more channels to the collection, user can by a package
  // so the OCL will be longer than PCL or remove channels
  // so the OCL will be shorter then PCL
  const length = Math.max(channels.length, personalLineup.length);

  for (let i = 0; i < length; i++) {
    const oclChannelId = channels[i]?.id;

    // we need to track all new channels and add them later
    if (oclChannelId && !pclChannels.has(oclChannelId)) {
      newLinears.push(channels[i]);
    }

    // when OCL is longer than PCL index could be out of range
    const pclChannel = personalLineup[i];

    if (!pclChannel) {
      continue;
    }

    // channel is no longer on collection
    if (!oclChannels.has(pclChannel.channelId)) {
      continue;
    }

    const linear = linearRepository.getLinearById(pclChannel.channelId);

    if (linear) {
      linears.push(linear);
    }
  }

  linears.push(...newLinears);

  return linears;
}
